import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { Wand2, Loader2, ShoppingBag, Layers, Sparkles, Check, Archive, Facebook, Instagram, ShieldCheck, Zap, Target, MousePointerClick, Eye, Megaphone, Bot, ChevronDown, ChevronUp, DollarSign, TrendingUp, Settings2 } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

export default function MetaCatalogAdsLab() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingSets, setLoadingSets] = useState(false);
  
  // Connections logic
  const [activeCatalog, setActiveCatalog] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [activeInstagram, setActiveInstagram] = useState(null);
  const [activePixel, setActivePixel] = useState(null);
  
  // Product Sets logic
  const [productSets, setProductSets] = useState([]);
  const [selectedProductSetId, setSelectedProductSetId] = useState('');

  const [budget, setBudget] = useState('500'); // Default 500
  const [createdResult, setCreatedResult] = useState(null);
  
  // Campaign Type: 'advantage_plus' or 'regular_sales'
  const [campaignType, setCampaignType] = useState('advantage_plus');

  // Smart Campaign Engine settings
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [budgetType, setBudgetType] = useState('daily'); // 'daily' or 'lifetime'
  const [lifetimeBudget, setLifetimeBudget] = useState('15000');
  const [bidStrategy, setBidStrategy] = useState('LOWEST_COST_WITHOUT_CAP');
  const [bidAmount, setBidAmount] = useState('');
  const [targetingInterests, setTargetingInterests] = useState('');
  const [customAudienceIds, setCustomAudienceIds] = useState('');

  // 1. Resolve Connections on mount
  useEffect(() => {
    if (selectedBrand?.id) {
      resolveConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  // 2. Fetch Product Sets when activeCatalog is set
  useEffect(() => {
    if (activeCatalog?.id) {
      fetchProductSets(activeCatalog.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCatalog?.id]);

  const resolveConnections = async () => {
    if (!selectedBrand?.id) return;
    try {
      const resp = await fetch(`${API_BASE}/api/auth/connections`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      const data = await resp.json();
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      
      // Find all relevant active connections
      const metaCatalogConn = connections.find((c) => c?.platform === 'META_CATALOG' && c?.status === 'ACTIVE');
      const facebookPageConn = connections.find((c) => c?.platform === 'FACEBOOK_PAGE' && c?.status === 'ACTIVE');
      const instagramConn = connections.find((c) => c?.platform === 'INSTAGRAM' && c?.status === 'ACTIVE');
      const metaPixelConn = connections.find((c) => c?.platform === 'META_PIXEL' && c?.status === 'ACTIVE');
      
      if (metaCatalogConn) {
        const catalogId = metaCatalogConn.customerId || metaCatalogConn.catalogId;
        setActiveCatalog({ 
          id: catalogId, 
          name: metaCatalogConn.accountName || metaCatalogConn.catalogName || 'Connected Catalog' 
        });
      } else {
        // Fallback: If no META_CATALOG connection, try fetching from meta-ads
        fetchCatalogsFallback();
      }

      if (facebookPageConn) {
        setActivePage({
          id: facebookPageConn.customerId,
          name: facebookPageConn.accountName || 'Facebook Page'
        });
      }

      if (instagramConn) {
        setActiveInstagram({
          id: instagramConn.customerId,
          name: instagramConn.accountName || 'Instagram Account'
        });
      }

      if (metaPixelConn) {
        setActivePixel({
          id: metaPixelConn.customerId,
          name: metaPixelConn.accountName || 'Meta Pixel'
        });
      }

    } catch (e) {
      console.error('Failed to resolve connections', e);
      fetchCatalogsFallback();
    }
  };

  const fetchCatalogsFallback = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/meta-ads/catalogs`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.catalogs) && data.catalogs.length > 0) {
        setActiveCatalog(data.catalogs[0]);
      } else if (data.catalogs && data.catalogs.length === 0) {
        toast.error('No catalogs found. Please connect your Meta Catalog first.');
      }
    } catch (e) {
      console.error('Failed to fetch catalogs fallback', e);
    }
  };

  const fetchProductSets = async (catalogId) => {
    try {
      setLoadingSets(true);
      const res = await fetch(`${API_BASE}/api/meta-ads/catalogs/${catalogId}/product-sets`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const data = await res.json();
      // The backend returns { ok: true, data: [...] }
      const list = Array.isArray(data?.data) ? data.data : [];
      setProductSets(list);
      
      // Auto-select first one if available
      if (list.length > 0) {
          setSelectedProductSetId(list[0].id);
      }
    } catch (e) {
      console.error('Failed to fetch product sets', e);
      toast.error('Failed to load product sets');
      setProductSets([]);
    } finally {
      setLoadingSets(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedBrand?.id) return;
    if (!activeCatalog?.id) {
      toast.error('No active catalog found');
      return;
    }
    if (!selectedProductSetId) {
      toast.error('Please select a product set');
      return;
    }

    const selectedSet = productSets.find(s => s.id === selectedProductSetId);
    if (!selectedSet) return;

    setLoading(true);
    setCreatedResult(null);

    try {
      // Build request body
      const requestBody = {
        dailyBudget: budget,
        catalogId: activeCatalog.id,
        productSetId: selectedSet.id,
        productSetName: selectedSet.name,
        pageId: activePage?.id,
        instagramActorId: activeInstagram?.id,
        pixelId: activePixel?.id,
        campaignType: campaignType
      };

      // Choose endpoint based on smart mode
      let endpoint = `${API_BASE}/api/labs/meta-ads/create-catalog-campaign`;

      if (smartEnabled) {
        endpoint = `${API_BASE}/api/labs/meta-campaigns/create-smart-campaign`;
        requestBody.smartEnabled = true;
        requestBody.connectionId = null; // resolveConnection will find it
        requestBody.instagramId = activeInstagram?.id;
        requestBody.instagramNumericId = activeInstagram?.numericId || '';
        if (budgetType === 'lifetime') {
          requestBody.lifetimeBudget = lifetimeBudget;
        }
        if (bidStrategy !== 'LOWEST_COST_WITHOUT_CAP') {
          requestBody.bidStrategy = bidStrategy;
          if (bidAmount && (bidStrategy === 'COST_CAP' || bidStrategy === 'BID_CAP')) {
            requestBody.bidAmount = bidAmount;
          }
        }
        if (targetingInterests.trim()) {
          try {
            requestBody.detailedTargeting = JSON.parse(targetingInterests);
          } catch {
            requestBody.detailedTargeting = { interests: targetingInterests.split(',').map(s => ({ name: s.trim() })) };
          }
        }
        if (customAudienceIds.trim()) {
          requestBody.customAudienceIds = customAudienceIds.trim();
        }
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Creation failed');

      if (data.success) {
        setCreatedResult(data);
        if (smartEnabled && !data.smart_registered) {
          toast.success('Campaign created on Meta.');
          toast.error('Smart Engine kaydı BAŞARISIZ — kampanya otomatik yönetilmeyecek.', { duration: 8000 });
        } else {
          toast.success(smartEnabled ? 'Smart Campaign Created & Registered!' : 'Campaign Created Successfully!');
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-indigo-600" />
          Glowytics Smart Catalog Campaign
        </h1>
        <p className="text-gray-500 mt-2 text-lg">
          Create a fully managed, best-practice Catalog Sales campaign in one click.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="p-8 grid gap-8">
          
          <div className={`rounded-xl p-6 border ${
            campaignType === 'advantage_plus' ? 'bg-indigo-50 border-indigo-100' : 
            campaignType === 'advantage_plus_vc' ? 'bg-violet-50 border-violet-100' :
            campaignType === 'regular_sales' ? 'bg-emerald-50 border-emerald-100' : 
            campaignType === 'awareness' ? 'bg-pink-50 border-pink-100' :
            'bg-amber-50 border-amber-100'
          }`}>
            <h3 className={`font-semibold flex items-center gap-2 ${
              campaignType === 'advantage_plus' ? 'text-indigo-900' : 
              campaignType === 'advantage_plus_vc' ? 'text-violet-900' :
              campaignType === 'regular_sales' ? 'text-emerald-900' : 
              campaignType === 'awareness' ? 'text-pink-900' :
              'text-amber-900'
            }`}>
              <Layers className="w-5 h-5" />
              {campaignType === 'advantage_plus' ? 'Advantage+ Purchase Campaign' : 
               campaignType === 'advantage_plus_vc' ? 'Advantage+ View Content Campaign' :
               campaignType === 'regular_sales' ? 'Regular Sales Campaign' : 
               campaignType === 'awareness' ? 'Awareness Campaign' :
               'Traffic Campaign'}
            </h3>
            <ul className={`mt-3 space-y-2 text-sm ${
              campaignType === 'advantage_plus' ? 'text-indigo-800/80' : 
              campaignType === 'advantage_plus_vc' ? 'text-violet-800/80' :
              campaignType === 'regular_sales' ? 'text-emerald-800/80' : 
              campaignType === 'awareness' ? 'text-pink-800/80' :
              'text-amber-800/80'
            }`}>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5" />
                <span><strong>Objective:</strong> {
                  campaignType === 'traffic' ? 'Traffic (Link Clicks)' : 
                  campaignType === 'awareness' ? 'Awareness (Reach)' :
                  'Sales (Conversions)'
                }</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5" />
                <span><strong>Optimization:</strong> {
                  campaignType === 'advantage_plus_vc' ? 'View Content (top funnel)' :
                  campaignType === 'traffic' ? 'Link Clicks' : 
                  campaignType === 'awareness' ? 'Reach & Impressions' :
                  'Purchase (bottom funnel)'
                }</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5" />
                <span><strong>Creative:</strong> Dynamic Carousel from your <strong>Product Set</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5" />
                {campaignType === 'advantage_plus' ? (
                  <span><strong>Best for:</strong> Bottom funnel, high-intent users</span>
                ) : campaignType === 'advantage_plus_vc' ? (
                  <span><strong>Best for:</strong> Mid funnel, product discovery</span>
                ) : campaignType === 'traffic' ? (
                  <span><strong>Best for:</strong> Website visits, catalog browsing</span>
                ) : campaignType === 'awareness' ? (
                  <span><strong>Best for:</strong> Brand recognition, new audiences</span>
                ) : (
                  <span><strong>Best for:</strong> Manual control, specific targeting</span>
                )}
              </li>
            </ul>
          </div>

          {/* Active Connections Info (Identity Verification) */}
          <div className="space-y-3">
            {activePage && (
              <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 shadow-sm transition-all hover:bg-white">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 flex-shrink-0">
                  <img src="/facebook-app-symbol.svg" alt="FB" className="w-5 h-5 grayscale" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Connected Facebook Page</p>
                  <p className="font-semibold truncate text-gray-900">{activePage.name}</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 uppercase border border-gray-200">Identity</span>
                </div>
              </div>
            )}

            {activeInstagram && (
              <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 shadow-sm transition-all hover:bg-white">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 flex-shrink-0">
                  <img src="/instagram.svg" alt="IG" className="w-5 h-5 grayscale" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Linked Instagram Profile</p>
                  <p className="font-semibold truncate text-gray-900">{activeInstagram.name}</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 uppercase border border-gray-200">IG Account</span>
                </div>
              </div>
            )}

            {activePixel && (
              <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 shadow-sm transition-all hover:bg-white">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 flex-shrink-0">
                  <img src="/facebookpixel.png" alt="Pixel" className="w-5 h-5 grayscale" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Active Conversion Pixel</p>
                  <p className="font-semibold truncate text-gray-900">{activePixel.name}</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 uppercase border border-gray-200">Tracking</span>
                </div>
              </div>
            )}

            {activeCatalog && (
              <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 shadow-sm transition-all hover:bg-white">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-gray-900 flex-shrink-0 border border-gray-200">
                  <ShoppingBag className="w-4 h-4 text-gray-900" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Active Product Catalog</p>
                  <p className="font-semibold truncate text-gray-900">{activeCatalog.name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Campaign Type Selector */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Campaign Type</label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {/* Advantage+ Purchase Option */}
              <button
                type="button"
                onClick={() => setCampaignType('advantage_plus')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  campaignType === 'advantage_plus'
                    ? 'border-indigo-500 bg-indigo-50/50 shadow-lg shadow-indigo-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {campaignType === 'advantage_plus' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-indigo-600" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    campaignType === 'advantage_plus' ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    <Zap className={`w-4 h-4 ${campaignType === 'advantage_plus' ? 'text-indigo-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${campaignType === 'advantage_plus' ? 'text-indigo-900' : 'text-gray-900'}`}>
                      A+ Purchase
                    </h4>
                  </div>
                </div>
                <ul className="text-[11px] text-gray-500 space-y-0.5">
                  <li>• AI optimization</li>
                  <li>• Purchase event</li>
                </ul>
              </button>

              {/* Advantage+ View Content Option */}
              <button
                type="button"
                onClick={() => setCampaignType('advantage_plus_vc')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  campaignType === 'advantage_plus_vc'
                    ? 'border-violet-500 bg-violet-50/50 shadow-lg shadow-violet-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {campaignType === 'advantage_plus_vc' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-violet-600" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    campaignType === 'advantage_plus_vc' ? 'bg-violet-100' : 'bg-gray-100'
                  }`}>
                    <Eye className={`w-4 h-4 ${campaignType === 'advantage_plus_vc' ? 'text-violet-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${campaignType === 'advantage_plus_vc' ? 'text-violet-900' : 'text-gray-900'}`}>
                      A+ View Content
                    </h4>
                  </div>
                </div>
                <ul className="text-[11px] text-gray-500 space-y-0.5">
                  <li>• AI optimization</li>
                  <li>• View content event</li>
                </ul>
              </button>

              {/* Regular Sales Option */}
              <button
                type="button"
                onClick={() => setCampaignType('regular_sales')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  campaignType === 'regular_sales'
                    ? 'border-emerald-500 bg-emerald-50/50 shadow-lg shadow-emerald-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {campaignType === 'regular_sales' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    campaignType === 'regular_sales' ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <Target className={`w-4 h-4 ${campaignType === 'regular_sales' ? 'text-emerald-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${campaignType === 'regular_sales' ? 'text-emerald-900' : 'text-gray-900'}`}>
                      Sales
                    </h4>
                  </div>
                </div>
                <ul className="text-[11px] text-gray-500 space-y-0.5">
                  <li>• Manual targeting</li>
                  <li>• Purchase event</li>
                </ul>
              </button>

              {/* Traffic Option */}
              <button
                type="button"
                onClick={() => setCampaignType('traffic')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  campaignType === 'traffic'
                    ? 'border-amber-500 bg-amber-50/50 shadow-lg shadow-amber-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {campaignType === 'traffic' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-amber-600" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    campaignType === 'traffic' ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    <MousePointerClick className={`w-4 h-4 ${campaignType === 'traffic' ? 'text-amber-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${campaignType === 'traffic' ? 'text-amber-900' : 'text-gray-900'}`}>
                      Traffic
                    </h4>
                  </div>
                </div>
                <ul className="text-[11px] text-gray-500 space-y-0.5">
                  <li>• Link clicks</li>
                  <li>• Website traffic</li>
                </ul>
              </button>

              {/* Awareness Option */}
              <button
                type="button"
                onClick={() => setCampaignType('awareness')}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  campaignType === 'awareness'
                    ? 'border-pink-500 bg-pink-50/50 shadow-lg shadow-pink-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {campaignType === 'awareness' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-pink-600" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    campaignType === 'awareness' ? 'bg-pink-100' : 'bg-gray-100'
                  }`}>
                    <Megaphone className={`w-4 h-4 ${campaignType === 'awareness' ? 'text-pink-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${campaignType === 'awareness' ? 'text-pink-900' : 'text-gray-900'}`}>
                      Awareness
                    </h4>
                  </div>
                </div>
                <ul className="text-[11px] text-gray-500 space-y-0.5">
                  <li>• Brand reach</li>
                  <li>• Max impressions</li>
                </ul>
              </button>
            </div>
          </div>

          {/* Campaign Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Product Set</label>
              {loadingSets ? (
                  <div className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-400 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading sets...
                  </div>
              ) : (
                <div className="relative">
                    <Archive className="absolute left-4 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
                    <select
                        value={selectedProductSetId}
                        onChange={e => setSelectedProductSetId(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white appearance-none"
                    >
                        {productSets.length === 0 && <option value="">No product sets found</option>}
                        {productSets.map(s => (
                        <option key={s.id} value={s.id}>
                            {s.name} ({s.product_count ?? '?'} products)
                        </option>
                        ))}
                    </select>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">Which products do you want to promote?</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Daily Budget</label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-400 font-bold">$</span>
                <input
                  type="number"
                  value={budget}
                  onChange={e => setBudget(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Currency matches your ad account</p>
            </div>
          </div>

          {/* Smart Campaign Engine Toggle */}
          <div className={`rounded-xl border-2 transition-all ${smartEnabled ? 'border-violet-400 bg-violet-50/30' : 'border-gray-200 bg-gray-50/30'}`}>
            <button
              type="button"
              onClick={() => setSmartEnabled(!smartEnabled)}
              className="w-full p-5 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${smartEnabled ? 'bg-violet-100' : 'bg-gray-100'}`}>
                  <Bot className={`w-5 h-5 ${smartEnabled ? 'text-violet-600' : 'text-gray-400'}`} />
                </div>
                <div className="text-left">
                  <h4 className={`font-bold text-sm ${smartEnabled ? 'text-violet-900' : 'text-gray-700'}`}>
                    Smart Campaign Engine
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    AI otomatik yönetim: bütçe optimizasyonu, kural tabanlı aksiyonlar, 7/24 izleme
                  </p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full transition-all flex items-center ${smartEnabled ? 'bg-violet-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                <div className="w-5 h-5 bg-white rounded-full shadow-md mx-1" />
              </div>
            </button>

            {smartEnabled && (
              <div className="px-5 pb-5 space-y-5 border-t border-violet-100">
                {/* Budget Type */}
                <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Budget Type</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setBudgetType('daily')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border-2 transition-all ${
                          budgetType === 'daily' ? 'border-violet-400 bg-violet-50 text-violet-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <DollarSign className="w-4 h-4 inline mr-1" />Daily
                      </button>
                      <button
                        type="button"
                        onClick={() => setBudgetType('lifetime')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border-2 transition-all ${
                          budgetType === 'lifetime' ? 'border-violet-400 bg-violet-50 text-violet-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <TrendingUp className="w-4 h-4 inline mr-1" />Lifetime
                      </button>
                    </div>
                  </div>

                  {budgetType === 'lifetime' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Lifetime Budget</label>
                      <div className="relative">
                        <span className="absolute left-4 top-3 text-gray-400 font-bold">$</span>
                        <input
                          type="number"
                          value={lifetimeBudget}
                          onChange={e => setLifetimeBudget(e.target.value)}
                          className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all text-sm"
                          placeholder="15000"
                        />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">30 gün boyunca toplam bütçe</p>
                    </div>
                  )}
                </div>

                {/* Bid Strategy */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Bid Strategy</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { value: 'LOWEST_COST_WITHOUT_CAP', label: 'Lowest Cost', desc: 'Meta otomatik optimize eder' },
                      { value: 'COST_CAP', label: 'Cost Cap', desc: 'Maks. CPA hedefi belirle' },
                      { value: 'BID_CAP', label: 'Bid Cap', desc: 'Maks. teklif sınırı' },
                      { value: 'MINIMUM_ROAS', label: 'Min. ROAS', desc: 'Minimum ROAS hedefi' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setBidStrategy(opt.value)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          bidStrategy === opt.value
                            ? 'border-violet-400 bg-violet-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className={`text-xs font-bold ${bidStrategy === opt.value ? 'text-violet-700' : 'text-gray-700'}`}>{opt.label}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                  {(bidStrategy === 'COST_CAP' || bidStrategy === 'BID_CAP' || bidStrategy === 'MINIMUM_ROAS') && (
                    <div className="mt-3">
                      <label className="block text-xs text-gray-500 mb-1">
                        {bidStrategy === 'MINIMUM_ROAS' ? 'Minimum ROAS (örn: 2.5)' : 'Bid/Cost Cap Amount ($)'}
                      </label>
                      <input
                        type="number"
                        value={bidAmount}
                        onChange={e => setBidAmount(e.target.value)}
                        className="w-48 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-violet-500 text-sm"
                        placeholder={bidStrategy === 'MINIMUM_ROAS' ? '2.5' : '500'}
                      />
                    </div>
                  )}
                </div>

                {/* Advanced Targeting (Collapsible) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Advanced Targeting
                    {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Interest Targeting (virgülle ayır)</label>
                        <input
                          type="text"
                          value={targetingInterests}
                          onChange={e => setTargetingInterests(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-violet-500 text-sm"
                          placeholder="online shopping, fashion, beauty"
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Meta interest kategorileri</p>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Custom Audience IDs (virgülle ayır)</label>
                        <input
                          type="text"
                          value={customAudienceIds}
                          onChange={e => setCustomAudienceIds(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-violet-500 text-sm"
                          placeholder="23851234567890, 23851234567891"
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Meta Ads Manager'dan audience ID'leri</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Smart Engine Info */}
                <div className="bg-violet-50 rounded-lg p-3 border border-violet-100">
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-violet-700 leading-relaxed">
                      <strong>Smart Engine Aktif:</strong> Kampanya oluşturulduktan sonra 3 gün öğrenme süreci başlar.
                      Ardından engine saatlik olarak performans verilerini kontrol eder ve kurallara göre otomatik aksiyon alır
                      (bütçe artırma/azaltma, durdurma vb.). Tüm aksiyonlar loglanır ve admin panelden izlenebilir.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Area */}
          <div className="pt-6 border-t border-gray-100 flex items-center justify-end">
            <button
              onClick={handleCreate}
              disabled={loading || !selectedProductSetId || !activeCatalog}
              className={`
                px-8 py-4 rounded-xl font-bold text-white shadow-lg shadow-indigo-500/30 flex items-center gap-3 transition-all transform active:scale-95
                ${loading || !selectedProductSetId ? 'bg-gray-300 cursor-not-allowed shadow-none' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'}
              `}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Campaign...
                </>
              ) : smartEnabled ? (
                <>
                  <Bot className="w-5 h-5" />
                  Launch Smart Campaign
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  Launch Campaign
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Result Card */}
      {createdResult && (
        <div className="mt-8 bg-green-50 rounded-2xl border border-green-100 p-6 animate-in slide-in-from-bottom-4">
          <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
            <Check className="w-5 h-5" />
            {createdResult.campaign_type || 'Campaign'} Created!
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-4 rounded-xl border border-green-100 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Campaign Name</div>
              <div className="text-lg font-medium text-gray-900 mt-1">{createdResult.campaign_name}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-green-100 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Campaign ID</div>
              <div className="text-lg font-mono text-gray-900 mt-1">{createdResult.campaign_id}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-green-100 shadow-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Type</div>
              <div className="text-lg font-medium text-gray-900 mt-1 flex items-center gap-2">
                {createdResult.campaign_type?.includes('View Content') ? (
                  <><Eye className="w-4 h-4 text-violet-600" /> {createdResult.campaign_type}</>
                ) : createdResult.campaign_type?.includes('Advantage') ? (
                  <><Zap className="w-4 h-4 text-indigo-600" /> {createdResult.campaign_type}</>
                ) : createdResult.campaign_type?.includes('Traffic') ? (
                  <><MousePointerClick className="w-4 h-4 text-amber-600" /> {createdResult.campaign_type}</>
                ) : createdResult.campaign_type?.includes('Awareness') ? (
                  <><Megaphone className="w-4 h-4 text-pink-600" /> {createdResult.campaign_type}</>
                ) : (
                  <><Target className="w-4 h-4 text-emerald-600" /> {createdResult.campaign_type}</>
                )}
              </div>
            </div>
          </div>
          {createdResult.smart_registered && (
            <div className="mb-4 bg-violet-50 p-3 rounded-xl border border-violet-100 flex items-center gap-2">
              <Bot className="w-4 h-4 text-violet-600" />
              <span className="text-sm text-violet-700">
                <strong>Smart Engine'e kaydedildi.</strong> 3 gün öğrenme süresi sonrası otomatik yönetim başlayacak.
                {' '}<a href="/hq/admin/smart-campaigns" className="underline font-medium">Admin Panel →</a>
              </span>
            </div>
          )}
          {createdResult.smart_register_error && (
            <div className="mb-4 bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-center gap-2">
              <Bot className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                <strong>Smart Engine kaydı başarısız.</strong> Kampanya Meta'da kuruldu ama otomatik yönetilmeyecek.
                <span className="block text-xs text-amber-600 mt-0.5">{createdResult.smart_register_error}</span>
              </span>
            </div>
          )}
          <p className="text-sm text-green-700 text-center">
            Campaign is currently <strong>PAUSED</strong>. Please review on Meta Ads Manager before enabling.
          </p>
        </div>
      )}
    </div>
  );
}
